import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { TenantProvider } from './contexts/TenantContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ColorPreview from './components/ColorPreview';
import Dashboard from './pages/Dashboard';
import Library from './pages/Library';
import Dhyana from './pages/Dhyana';
import Notes from './pages/Notes';
import SingleNoteView from './pages/SingleNoteView';
import GeneralChat from './pages/GeneralChat';
import Bookmarks from './pages/Bookmarks';
import ComingSoon from './pages/ComingSoon';
import NotFound from './pages/NotFound';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import InstitutionRoute from './components/InstitutionRoute';
import AdminReferrals from './pages/AdminReferrals';
import AdminInstitutions from './pages/AdminInstitutions';
import MyBookRequests from './pages/MyBookRequests';
import AdminBookApprovals from './pages/AdminBookApprovals';
import InstitutionSelector from './pages/InstitutionSelector';
import InstitutionHome from './pages/InstitutionHome';
import InstitutionGroupDetail from './pages/InstitutionGroupDetail';
import CourseStudio from './pages/CourseStudio';
import AssignmentStudio, { AssignmentViewer } from './pages/AssignmentStudio';
import MyCourses from './pages/MyCourses';
import VideoLibrary from './pages/VideoLibrary';
import VideoWatch from './pages/VideoWatch';
import Paathshaala from './pages/Paathshaala';
import ReviewDesk from './pages/inbde/ReviewDesk';
import Playbook from './pages/Playbook';
import PlaybookView from './pages/PlaybookView';
import Analytics from './pages/Analytics';
import AnalyticsStudentDetail from './pages/AnalyticsStudentDetail';
import AnalyticsLive from './pages/AnalyticsLive';
import AnalyticsStudentDetailLive from './pages/AnalyticsStudentDetailLive';
import { usePageTracking } from './hooks/usePageTracking';
import { useTenantBranding } from './hooks/useTenantBranding';
import { useTenant } from './contexts/TenantContext';
import { TenantSplash } from './components/common/TenantSplash';
import { getBrand } from './config/api';

// Keep local draft/synthetic previews and their answer fixtures out of production builds.
const InbdeQuestionPreview = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/inbde/QuestionPreview'))
  : null;
const InbdePlatformPreview = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/inbde/PlatformPreview'))
  : null;
const InbdeLearnerPlatform = React.lazy(() => import('./pages/inbde/LearnerPlatform'));

// Get Google Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Debug: Log the client ID (remove this in production)
console.log('Google Client ID:', GOOGLE_CLIENT_ID ? 'Found' : 'Missing');

function AppContent() {
  usePageTracking();
  useTenantBranding();
  const { isLoading, isTenantSubdomain } = useTenant();

  // Cold-cache bootstrap takes ~50-150ms. Block route render with a neutral
  // splash so users on tenant subdomains don't see Vidya defaults flash.
  // Cache hits skip this entirely (isLoading starts false).
  if (isLoading && isTenantSubdomain) {
    return <TenantSplash />;
  }

  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={getBrand() === 'inbde'
          ? <Navigate to="/inbde" replace /> : <Landing />} />
        {InbdeQuestionPreview && <Route path="/inbde/preview" element={
          <React.Suspense fallback={<p role="status">Loading practice preview…</p>}>
            <InbdeQuestionPreview />
          </React.Suspense>
        } />}
        {InbdePlatformPreview && <Route path="/inbde/preview/app/*" element={
          <React.Suspense fallback={<p role="status">Loading study space preview…</p>}><InbdePlatformPreview /></React.Suspense>
        } />}
        <Route path="/inbde/*" element={<React.Suspense fallback={<p role="status">Loading study space…</p>}><InbdeLearnerPlatform /></React.Suspense>} />
        <Route path="/colors" element={<ColorPreview />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/paathshaala" element={<Paathshaala />} />
        {/* Expert review desk — access is gated server-side by the exam_reviewers roster,
            so this route only needs a signed-in user. */}
        <Route path="/review" element={<PrivateRoute><ReviewDesk /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/library" element={<PrivateRoute><Library /></PrivateRoute>} />
        <Route path="/bookmarks" element={<PrivateRoute><Bookmarks /></PrivateRoute>} />
        <Route path="/dhyana/:bookId" element={<PrivateRoute><Dhyana /></PrivateRoute>} />
        <Route path="/library/:bookId/notes" element={<PrivateRoute><Notes /></PrivateRoute>} />
        <Route path="/playbook" element={<PrivateRoute><Playbook /></PrivateRoute>} />
        <Route path="/playbook/view/:noteId" element={<PlaybookView />} />
        <Route path="/library/:bookId/note/:noteId" element={<PrivateRoute><SingleNoteView /></PrivateRoute>} />
        <Route path="/note/:noteId" element={<PrivateRoute><SingleNoteView /></PrivateRoute>} />
        <Route path="/chat" element={<PrivateRoute><GeneralChat /></PrivateRoute>} />
        <Route path="/my-requests" element={<PrivateRoute><MyBookRequests /></PrivateRoute>} />
        {/* Analytics — Mock (kept for demos) */}
        <Route path="/analytics/:instituteId" element={<Analytics />} />
        <Route path="/analytics/:instituteId/student/:studentId" element={<AnalyticsStudentDetail />} />
        {/* Analytics — Live (wired to real API) */}
        <Route
          path="/analytics-live/:instituteId/group/:groupId/book/:bookId"
          element={<PrivateRoute><AnalyticsLive /></PrivateRoute>}
        />
        <Route
          path="/analytics-live/:instituteId/group/:groupId/book/:bookId/student/:userId"
          element={<PrivateRoute><AnalyticsStudentDetailLive /></PrivateRoute>}
        />
        {/* Admin routes */}
        <Route path="/admin/referrals" element={<AdminRoute><AdminReferrals /></AdminRoute>} />
        <Route path="/admin/institutions" element={<AdminRoute><AdminInstitutions /></AdminRoute>} />
        <Route path="/admin/book-approvals" element={<AdminRoute><AdminBookApprovals /></AdminRoute>} />
        {/* Institution routes */}
        <Route path="/institution" element={<InstitutionRoute><InstitutionSelector /></InstitutionRoute>} />
        <Route path="/institution/:id" element={<InstitutionRoute><InstitutionHome /></InstitutionRoute>} />
        <Route path="/institution/:id/groups/:groupId" element={<InstitutionRoute><InstitutionGroupDetail /></InstitutionRoute>} />
        {/* Course Studio (teacher) */}
        <Route path="/teacher/courses" element={<PrivateRoute><CourseStudio /></PrivateRoute>} />
        <Route path="/teacher/courses/:courseId" element={<PrivateRoute><CourseStudio /></PrivateRoute>} />
        {/* Assignment Studio (teacher) */}
        <Route path="/assignment-studio" element={<PrivateRoute><AssignmentStudio /></PrivateRoute>} />
        <Route path="/assignment-studio/:id" element={<PrivateRoute><AssignmentStudio /></PrivateRoute>} />
        <Route path="/assignments/:id/view" element={<PrivateRoute><AssignmentViewer /></PrivateRoute>} />
        {/* Video Library — one surface, role-branched (managers manage, students watch what's shared) */}
        <Route path="/video-library" element={<PrivateRoute><VideoLibrary /></PrivateRoute>} />
        <Route path="/watch/:videoId" element={<PrivateRoute><VideoWatch /></PrivateRoute>} />
        {/* My Courses (student) */}
        <Route path="/my-courses" element={<PrivateRoute><MyCourses /></PrivateRoute>} />
        <Route path="/my-courses/:courseId" element={<PrivateRoute><MyCourses /></PrivateRoute>} />
        {/* Guide pages — served as static HTML from public/ */}
        <Route path="/howtochat" element={<Navigate to="/howtochat/" replace />} />
        <Route path="/howtoread" element={<Navigate to="/howtoread/" replace />} />
        {/* Coming Soon routes */}
        <Route path="/docs" element={<ComingSoon />} />
        <Route path="/help" element={<ComingSoon />} />
        <Route path="/blog" element={<ComingSoon />} />
        <Route path="/api" element={<ComingSoon />} />
        <Route path="/about" element={<ComingSoon />} />
        <Route path="/careers" element={<ComingSoon />} />
        <Route path="/contact" element={<ComingSoon />} />
        <Route path="/press" element={<ComingSoon />} />
        <Route path="/privacy" element={<ComingSoon />} />
        <Route path="/terms" element={<ComingSoon />} />
        <Route path="/cookies" element={<ComingSoon />} />
        <Route path="/pricing" element={<ComingSoon />} />
        {/* 404 catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <TenantProvider>
          <ToastProvider>
            <Router>
              <AppContent />
            </Router>
          </ToastProvider>
        </TenantProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
